import { getConfig } from '@/configs/config-accessor';
import { handleGetCollection } from '@/constants/db-collection.constants';
import ApiService from './ApiService';

// Get the API prefix from the config accessor
const getApiPrefix = () => getConfig().newApiPrefix;

export interface IDataSource {
  _id: string;
  displayName: string;
  baseUrl?: string;
  queryUrl?: string;
  cacheTime?: string;
  slug: string;
  code: string;
  isReserved?: boolean;
  dataConfig?: {
    defaultFetchLimit: number;
  };
  type: { label: string; value: string };
  navigationKey: string | null;
  workflowConfig: {
    isEnabled: boolean;
    allowedFields: { label: string; value: string }[];
  };
  pageConfig: {
    type: 'form' | 'list' | 'pivot' | 'kanban' | 'grid-list' | 'dashboard' | 'classic-list';
    isEnabled: boolean;
    config: { pageId: string | null; jsonId: string | null } | null;
  }[];
  fields: {
    name: string;
    type: string;
    value: unknown;
    extra?: Record<string, unknown>;
  }[];
}

export type CreateDataSource_Body = Omit<IDataSource, '_id'>;
export type CreateDataSource_Response = IResponse<IDataSource | null>;
export const createDataSource = async (body: CreateDataSource_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<CreateDataSource_Response, DynamicCreate_Body<CreateDataSource_Body>>({ 
    url: `${newApiPrefix}/dynamicmongodb/insert`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('data-source'), 
      data: body 
    } 
  });
};

export type GetDataSource_Response = IResponse<IDataSource[] | null>;
export const getAllDataSources = async () => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<GetDataSource_Response, DynamicGet_Body>({ 
    url: `${newApiPrefix}/dynamicmongodb/get`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('data-source'), 
      sort: { displayName: 'asc' } 
    } 
  });
};

export type UpdateDataSource_Params = { _id: string };
export type UpdateDataSource_Body = Partial<Omit<IDataSource, '_id'>>;
export type UpdateDataSource_Response = IResponse<IDataSource | null>;
export const updateDataSources = async (params: UpdateDataSource_Params, body: UpdateDataSource_Body) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<UpdateDataSource_Response, DynamicUpdate_Body<UpdateDataSource_Body>>({ 
    url: `${newApiPrefix}/dynamicmongodb/update`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('data-source'), 
      filter: { _id: { $oid: params._id } }, 
      data: body 
    } 
  });
};

export type DeleteDataSource_Params = { _id: string };
export type DeleteDataSource_Response = IResponse<IDataSource | null>;
export const deleteDataSources = async (params: DeleteDataSource_Params) => {
  const newApiPrefix = getApiPrefix();
  return ApiService.fetchData<DeleteDataSource_Response, DynamicDelete_Body>({ 
    url: `${newApiPrefix}/dynamicmongodb/delete`, 
    method: 'post', 
    data: { 
      collection: handleGetCollection('data-source'), 
      filter: { _id: params._id } 
    } 
  });
};
